import { useEffect, useState } from 'react';

/**
 * Single module-level 1s interval shared by all subscribers. Cards subscribe
 * via `useSharedTicker()` to re-render once per second without spawning their
 * own timers. The interval is torn down when the last subscriber unmounts.
 */

const TICK_INTERVAL_MS = 1_000;

const listeners = new Set<(now: number) => void>();
let timer: ReturnType<typeof setInterval> | null = null;

function ensureRunning(): void {
  if (timer) return;
  timer = setInterval(() => {
    const now = Date.now();
    listeners.forEach((fn) => fn(now));
  }, TICK_INTERVAL_MS);
}

function maybeStop(): void {
  if (listeners.size === 0 && timer) {
    clearInterval(timer);
    timer = null;
  }
}

/** Returns a `Date.now()` snapshot that refreshes every second. */
export function useSharedTicker(): number {
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const fn = (next: number) => setNow(next);
    listeners.add(fn);
    ensureRunning();
    return () => {
      listeners.delete(fn);
      maybeStop();
    };
  }, []);

  return now;
}
